import {
  Activity,
  Boxes,
  Building2,
  Check,
  Cloud,
  Copy,
  Code2,
  Database,
  ExternalLink,
  FileCode2,
  FlaskConical,
  GitBranch,
  GitCommitHorizontal,
  Layers,
  ListChecks,
  Mail,
  ShieldCheck,
  Rocket,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react'
import { useContext, useState, type ReactNode } from 'react'
import { ToastContext } from '@/components/ui/toast'
import { HoverPopover } from '@/features/publish/components/HoverPopover'
import type {
  LifecycleViewField,
  LifecycleViewGroup,
  LifecycleViewStage,
} from '@/features/lifecycle/resources/microservice/model/microservice-view'
import { brandFor, type Brand } from './brands'
import { presentField, type Presented, type Tone } from './field-presentation'

/** A brand's logo, drawn from its SVG in the brand's own colour (or the text colour where that would disappear on a theme). */
export function BrandLogo({
  brand,
  className = 'size-4',
}: {
  brand: Brand
  className?: string
}) {
  const source = `/assets/brands/${brand.slug}.svg`
  if (brand.multicolor)
    return (
      <img
        src={source}
        alt={brand.name}
        data-brand={brand.slug}
        className={`inline-block shrink-0 object-contain ${className}`}
      />
    )
  return (
    <span
      role="img"
      aria-label={brand.name}
      data-brand={brand.slug}
      className={`inline-block shrink-0 ${className}`}
      style={{
        backgroundColor: brand.color ?? 'currentColor',
        maskImage: `url(${source})`,
        WebkitMaskImage: `url(${source})`,
        maskSize: 'contain',
        WebkitMaskSize: 'contain',
        maskRepeat: 'no-repeat',
        WebkitMaskRepeat: 'no-repeat',
        maskPosition: 'center',
        WebkitMaskPosition: 'center',
      }}
    />
  )
}

const toneStyles: Record<Tone, string> = {
  success: 'border-status-success/30 bg-status-success/10 text-status-success',
  failed: 'border-status-failed/30 bg-status-failed/10 text-status-failed',
  running: 'border-status-warning/30 bg-status-warning/10 text-status-warning',
  info: 'border-status-assessed/30 bg-status-assessed/10 text-status-assessed',
  neutral: 'border-border-default bg-inset text-secondary',
}
const toneDots: Record<Tone, string> = {
  success: 'bg-status-success',
  failed: 'bg-status-failed',
  running: 'bg-status-warning',
  info: 'bg-status-assessed',
  neutral: 'bg-border-strong',
}

const groupIcons: Array<[RegExp, LucideIcon]> = [
  [/stakeholder|consumer|team/i, Users],
  [/application|identity|overview|context/i, Building2],
  [/source control|branching|pull request/i, GitBranch],
  [/infrastructure|cloud/i, Cloud],
  [/database/i, Database],
  [/runtime|observability|quality metrics|analyzer/i, Activity],
  [/security|reviews? &|approval|^reviews$/i, ShieldCheck],
  [/generation|delivery|merge outcome/i, Rocket],
  [/test|coverage|readiness|postman|scenario/i, FlaskConical],
  [/specification|design|linting|requirement|metadata/i, FileCode2],
  [/mock/i, Boxes],
  [/finding|rule|scan|analysis/i, ListChecks],
]

/** The brand that best stands for a whole group: its framework, source provider, cloud or database. */
function groupBrand(group: LifecycleViewGroup): Brand | null {
  const wanted =
    /source control|infrastructure|database|application metadata|server metadata/i
  if (!wanted.test(group.title)) return null
  for (const label of [
    'Provider',
    'Cloud Provider',
    'Type',
    'Framework',
    'Language',
  ]) {
    const field = group.fields.find((item) => item.label === label)
    const brand = field ? brandFor(field.value) : null
    if (brand) return brand
  }
  return null
}

function GroupIcon({ group }: { group: LifecycleViewGroup }) {
  const brand = groupBrand(group)
  if (brand)
    return (
      <span className="grid size-9 shrink-0 place-items-center rounded-xl border border-border-default bg-surface">
        <BrandLogo brand={brand} className="size-5" />
      </span>
    )
  const Icon =
    groupIcons.find(([pattern]) => pattern.test(group.title))?.[1] ?? Layers
  return (
    <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-accent-faint text-accent">
      <Icon className="size-4" />
    </span>
  )
}

function Chip({
  children,
  tone = 'neutral',
}: {
  children: ReactNode
  tone?: Tone
}) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${toneStyles[tone]}`}
    >
      {children}
    </span>
  )
}

/** A value longer than this is cut to one line and shown in full in a popover on hover. */
const inlineLimit = 44
/** Running text (a description, an explanation) is cut to a few lines instead. */
const paragraphLimit = 160
const visibleChips = 5

/** The full text in the app's hover popover (never the browser's own title tip). */
function FullText({ text, children }: { text: string; children: ReactNode }) {
  return (
    <HoverPopover trigger={children} width="w-max max-w-md">
      <p className="text-xs leading-5 break-words whitespace-pre-wrap text-primary">
        {text}
      </p>
    </HoverPopover>
  )
}

/** Copies a value: a small button that turns into a tick for a moment, with a toast when the app shows them. */
function CopyButton({ value, label }: { value: string; label: string }) {
  const toast = useContext(ToastContext)
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      toast?.success(`${label} copied to your clipboard.`, { title: 'Copied' })
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      toast?.error(`${label} could not be copied.`, { title: 'Copy failed' })
    }
  }
  return (
    <button
      type="button"
      aria-label={`Copy ${label}`}
      onClick={() => void copy()}
      className="grid size-6 shrink-0 place-items-center rounded-md text-muted opacity-0 transition hover:bg-inset hover:text-primary focus-visible:opacity-100 group-focus-within/field:opacity-100 group-hover/field:opacity-100"
    >
      {copied ? (
        <Check className="size-3.5 text-status-success" />
      ) : (
        <Copy className="size-3.5" />
      )}
    </button>
  )
}

/** A one-line value; its full text appears on hover when it was long enough to be cut. */
function OneLine({
  text,
  className,
  children,
}: {
  text: string
  className: string
  children?: ReactNode
}) {
  const cut = text.length > inlineLimit
  const body = (
    <span className={`${className} ${cut ? 'block max-w-full truncate' : ''}`}>
      {children ?? text}
    </span>
  )
  return cut ? <FullText text={text}>{body}</FullText> : body
}

function Value({ presented, label }: { presented: Presented; label: string }) {
  switch (presented.kind) {
    case 'empty':
      return <span className="text-sm text-muted italic">{presented.text}</span>
    case 'status':
      return (
        <Chip tone={presented.tone}>
          <span
            className={`size-1.5 rounded-full ${toneDots[presented.tone]}`}
          />
          {presented.text}
        </Chip>
      )
    case 'link':
      return (
        <span className="flex min-w-0 items-center gap-1">
          <FullText text={presented.text}>
            <a
              href={presented.href}
              target="_blank"
              rel="noreferrer"
              className="inline-flex min-w-0 max-w-full items-center gap-1.5 text-sm font-medium text-accent underline-offset-2 hover:underline"
            >
              <span className="truncate">{presented.text}</span>
              <ExternalLink className="size-3.5 shrink-0" />
            </a>
          </FullText>
          <CopyButton value={presented.text} label={label} />
        </span>
      )
    case 'email':
      return (
        <span className="flex min-w-0 items-center gap-1">
          <a
            href={`mailto:${presented.text}`}
            className="inline-flex min-w-0 items-center gap-1.5 text-sm font-medium text-accent underline-offset-2 hover:underline"
          >
            <Mail className="size-3.5 shrink-0" />
            <span className="truncate">{presented.text}</span>
          </a>
          <CopyButton value={presented.text} label={label} />
        </span>
      )
    case 'flag':
      return (
        <Chip
          tone={
            presented.on === true
              ? 'success'
              : presented.on === false
                ? 'neutral'
                : 'info'
          }
        >
          {presented.on === true ? (
            <Check className="size-3" />
          ) : presented.on === false ? (
            <X className="size-3" />
          ) : null}
          {presented.text}
        </Chip>
      )
    case 'chips': {
      const shown = presented.items.slice(0, visibleChips)
      const rest = presented.items.slice(visibleChips)
      return (
        <span className="flex flex-wrap items-center gap-1.5">
          {shown.map((item) => (
            <Chip key={item.text}>
              {item.brand && (
                <BrandLogo brand={item.brand} className="size-3.5" />
              )}
              <span className="max-w-48 truncate">{item.text}</span>
            </Chip>
          ))}
          {rest.length > 0 && (
            <HoverPopover
              trigger={<Chip tone="info">+{rest.length} more</Chip>}
              width="w-max max-w-md"
            >
              <ul className="flex flex-wrap gap-1.5">
                {rest.map((item) => (
                  <li key={item.text}>
                    <Chip>
                      {item.brand && (
                        <BrandLogo brand={item.brand} className="size-3.5" />
                      )}
                      {item.text}
                    </Chip>
                  </li>
                ))}
              </ul>
            </HoverPopover>
          )}
        </span>
      )
    }
    case 'brand':
      return (
        <OneLine
          text={presented.text}
          className="inline-flex items-center gap-2 text-sm font-semibold text-primary"
        >
          <BrandLogo brand={presented.brand} className="size-5 shrink-0" />
          <span className="truncate">{presented.text}</span>
        </OneLine>
      )
    case 'code':
      return (
        <span className="flex min-w-0 items-center gap-1">
          <OneLine
            text={presented.text}
            className="inline-flex min-w-0 items-center gap-1.5 rounded-md bg-inset px-2 py-1 font-mono text-xs text-primary"
          >
            {presented.icon === 'branch' && (
              <GitBranch className="size-3.5 shrink-0 text-muted" />
            )}
            {presented.icon === 'commit' && (
              <GitCommitHorizontal className="size-3.5 shrink-0 text-muted" />
            )}
            <span className="truncate">{presented.text}</span>
          </OneLine>
          <CopyButton value={presented.text} label={label} />
        </span>
      )
    case 'metric':
      return (
        <span className="text-base font-semibold text-primary tabular-nums">
          {presented.text}
        </span>
      )
    default:
      return presented.text.length > paragraphLimit ? (
        <FullText text={presented.text}>
          <span className="line-clamp-3 text-sm leading-5 font-medium break-words text-primary">
            {presented.text}
          </span>
        </FullText>
      ) : presented.text.length > inlineLimit &&
        !presented.text.includes('\n') ? (
        <OneLine
          text={presented.text}
          className="text-sm leading-5 font-medium text-primary"
        />
      ) : (
        <span className="text-sm leading-5 font-medium break-words whitespace-pre-wrap text-primary">
          {presented.text}
        </span>
      )
  }
}

function Field({ field }: { field: LifecycleViewField }) {
  const presented = presentField(field)
  return (
    <div className={`group/field min-w-0 ${field.wide ? 'sm:col-span-2' : ''}`}>
      <dt className="text-[11px] font-medium tracking-wide text-muted uppercase">
        {field.label}
      </dt>
      <dd className="mt-1 min-w-0">
        <Value presented={presented} label={field.label} />
      </dd>
    </div>
  )
}

/** One group of a lifecycle view: its icon (or the logo of the technology it configures), title and every field. */
export function LifecycleDataGroup({ group }: { group: LifecycleViewGroup }) {
  return (
    <section className="min-w-0 rounded-xl border border-border-default/70 bg-raised/55 p-4">
      <header className="flex items-center gap-3">
        <GroupIcon group={group} />
        <h3 className="text-sm font-semibold text-primary">{group.title}</h3>
      </header>
      <dl className="mt-4 grid gap-x-6 gap-y-4 sm:grid-cols-2">
        {group.fields.map((field) => (
          <Field key={field.label} field={field} />
        ))}
      </dl>
    </section>
  )
}

/**
 * The technologies a resource is built with and runs on, as logos: read from the development stage's own fields,
 * so a chip appears only for something the resource really has.
 */
export function TechStack({ stages }: { stages: LifecycleViewStage[] }) {
  const fields = stages
    .find((stage) => stage.id === 'development')
    ?.groups.flatMap((group) =>
      group.fields.map((field) => ({ ...field, group: group.title })),
    )
  if (!fields) return null
  const wanted: Array<[string, string]> = [
    ['Language', 'Language'],
    ['Framework', 'Framework'],
    ['Java Version', 'Java'],
    ['Provider', 'Source control'],
    ['Cloud Provider', 'Cloud'],
    ['Deployment Targets', 'Runs on'],
    ['Deployment Target', 'Runs on'],
    ['Type', 'Database'],
  ]
  const chips: Array<{ caption: string; text: string; brand: Brand | null }> =
    []
  for (const [label, caption] of wanted) {
    const field = fields.find(
      (item) =>
        item.label === label && (label !== 'Type' || item.group === 'Database'),
    )
    if (!field) continue
    const presented = presentField(field)
    if (presented.kind === 'empty') continue
    if (presented.kind === 'chips') {
      for (const item of presented.items)
        chips.push({ caption, text: item.text, brand: item.brand })
    } else if (presented.kind === 'brand') {
      chips.push({
        caption,
        text:
          label === 'Java Version' ? `Java ${presented.text}` : presented.text,
        brand: presented.brand,
      })
    }
  }
  const seen = new Set<string>()
  const unique = chips.filter((chip) => {
    const key = `${chip.caption}:${chip.text}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
  if (unique.length === 0) return null
  return (
    <div className="mt-5 border-t border-border-default/60 pt-4">
      <p className="mb-2.5 flex items-center gap-2 text-[11px] font-semibold tracking-[0.14em] text-muted uppercase">
        <Code2 className="size-3.5" /> Tech stack
      </p>
      <ul className="flex flex-wrap gap-2">
        {unique.map((chip) => (
          <li
            key={`${chip.caption}:${chip.text}`}
            className="inline-flex items-center gap-2 rounded-xl border border-border-default bg-surface px-3 py-1.5"
          >
            {chip.brand ? (
              <BrandLogo brand={chip.brand} className="size-4" />
            ) : (
              <Layers className="size-4 text-muted" />
            )}
            <span className="text-xs font-semibold text-primary">
              {chip.text}
            </span>
            <span className="text-[10px] text-muted">{chip.caption}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
